import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Placement = 'top' | 'bottom';

/**
 * LTTooltip
 * - Portals to <body> (never clipped by overflow-hidden cards)
 * - Supports hover + keyboard focus
 * - Wraps long text (readable)
 */
export function LTTooltip({
  content,
  children,
  placement = 'top',
  maxWidth = 520,
}: {
  content: React.ReactNode;
  children: React.ReactElement;
  placement?: Placement;
  maxWidth?: number;
}) {
  const id = useId();
  const anchorRef = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const mergedChild = useMemo(() => {
    return React.cloneElement(children, {
      ref: (node: HTMLElement) => {
        anchorRef.current = node;
        const anyRef: any = (children as any).ref;
        if (typeof anyRef === 'function') anyRef(node);
        else if (anyRef && typeof anyRef === 'object') anyRef.current = node;
      },
      onMouseEnter: (e: any) => {
        children.props?.onMouseEnter?.(e);
        setOpen(true);
      },
      onMouseLeave: (e: any) => {
        children.props?.onMouseLeave?.(e);
        setOpen(false);
      },
      onFocus: (e: any) => {
        children.props?.onFocus?.(e);
        setOpen(true);
      },
      onBlur: (e: any) => {
        children.props?.onBlur?.(e);
        setOpen(false);
      },
      'aria-describedby': open ? id : undefined,
    });
  }, [children, id, open]);

  useLayoutEffect(() => {
    if (!open) return;
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();

    const margin = 12;
    const viewportW = window.innerWidth;
    const left = Math.max(margin, Math.min(viewportW - margin, r.left + r.width / 2));
    const top = placement === 'bottom' ? r.bottom + 10 : r.top - 10;
    setPos({ top, left });
  }, [open, placement, content]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => {
      try {
        const el = anchorRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const margin = 12;
        const viewportW = window.innerWidth;
        const left = Math.max(margin, Math.min(viewportW - margin, r.left + r.width / 2));
        const top = placement === 'bottom' ? r.bottom + 10 : r.top - 10;
        setPos({ top, left });
      } catch {}
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, placement]);

  return (
    <>
      {mergedChild}
      {open
        ? createPortal(
            <div
              id={id}
              role="tooltip"
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                transform: placement === 'bottom' ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
                maxWidth,
                zIndex: 2000,
                pointerEvents: 'none',
              }}
            >
              <div className="whitespace-pre-wrap rounded-xl border border-slate-200 bg-white/95 px-3 py-2 text-xs font-semibold leading-snug text-slate-800 shadow-xl backdrop-blur dark:border-neutral-800 dark:bg-neutral-950/95 dark:text-neutral-100">
                {content}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

